export const LAUNCH_ACADEMIC_YEAR_ID = 'a4000000-0000-4000-8000-000000000001';
export const LAUNCH_CURRICULUM_ID = 'a1000000-0000-4000-8000-000000000001';
export const LAUNCH_ACADEMIC_LEVEL_ID = 'a3000000-0000-4000-8000-000000000001';
export const DEFAULT_COUNTRY_CODE = 'IN';

const RUPEE_GROUPS = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

/**
 * Format integer paise for display.
 *
 * The whole-rupee part is grouped as a BigInt and the paise are appended as
 * text, so the value never passes through a JS `number`. The previous version
 * did `Number(rupees)` before formatting, which is exact for realistic amounts
 * but puts a float in a money path — the one place the codebase has been
 * strict about not doing that. Keeping it integer-only here means the rule
 * holds on both sides of the wire, with no judgement call about how large an
 * amount has to get before it matters.
 */
export function formatInrMinor(minor: string): string {
  const paise = BigInt(minor);
  const negative = paise < 0n;
  const absolute = negative ? -paise : paise;

  const whole = absolute / 100n;
  const fraction = (absolute % 100n).toString().padStart(2, '0');

  return `${negative ? '-' : ''}₹${RUPEE_GROUPS.format(whole)}.${fraction}`;
}

/** Parse a rupee amount typed by staff into integer paise — never via JS number. */
export function rupeesInputToMinorString(input: string): string {
  const trimmed = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    throw new Error('Enter a valid amount in rupees (up to two decimal places).');
  }
  const [whole = '0', fraction = ''] = trimmed.split('.');
  const paisePart = fraction.padEnd(2, '0').slice(0, 2);
  return (BigInt(whole) * 100n + BigInt(paisePart || '0')).toString();
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  REGISTRATION_PENDING_PAYMENT: 'Registration fee due',
  REGISTERED: 'Registered',
  UNDER_REVIEW: 'Under review',
  PAYMENT_PENDING: 'Payment pending',
  PAID: 'Paid',
  ADMISSION_PROVISIONAL: 'Admission fee paid',
  ADMISSION_CONFIRMED: 'Admission confirmed',
  QUOTATION_ISSUED: 'Quotation issued',
  CHANGES_REQUESTED: 'Changes requested',
  AWAITING_PARENT_DECISION: 'Partial approval — decide',
  APPROVAL_COMPLETE: 'Approved',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
};

export function formatApplicationStatus(status: string): string {
  return STATUS_LABELS[status] ?? status.replaceAll('_', ' ').toLowerCase();
}
